<template>
  <div class="ytcid-detail">
    <div class="vx-col w-full mb-base">
      <vx-card class="w-full ytcid-detail-card">
        <!-- Top breadcrumb / actions bar -->
        <div class="yt-topbar">
          <router-link to="/youtube-cid" class="yt-back">
            <i class="el-icon-arrow-left"></i>
            <span>저작물</span>
          </router-link>
          <div class="yt-topbar__right">
            <a v-if="cmsUrl" :href="cmsUrl" target="_blank" rel="noopener" class="yt-link-out">
              YouTube CMS에서 열기 ↗
            </a>
            <button class="yt-btn yt-btn--ghost" type="button" disabled>변경사항 실행취소</button>
            <button class="yt-btn yt-btn--primary" type="button" disabled>저장</button>
          </div>
        </div>

        <!-- Merged asset notice -->
        <div v-if="isMerged" class="yt-banner">
          <i class="el-icon-info"></i>
          <span>병합된 저작물입니다.</span>
          <span v-if="aliasIds.length" class="yt-banner__aliases">
            (별칭 {{ aliasIds.length }}개)
          </span>
        </div>

        <!-- Page tabs -->
        <div class="yt-page-tabs">
          <button
            class="yt-tab"
            :class="{ 'is-active': pageTab === 'overview' }"
            type="button"
            @click="pageTab = 'overview'"
          >개요</button>
          <button
            class="yt-tab"
            :class="{ 'is-active': pageTab === 'shares' }"
            type="button"
            @click="pageTab = 'shares'"
          >음원 공유 ({{ sharesCount }})</button>
        </div>

        <!-- Loading / not found -->
        <div v-if="loading" class="yt-state">불러오는 중…</div>
        <div v-else-if="!asset" class="yt-state">저작물을 찾을 수 없습니다.</div>

        <!-- Body -->
        <div v-else class="yt-body">
          <!-- Left sidebar -->
          <aside class="yt-sidebar">
            <div class="yt-side-hero">
              <div class="yt-side-thumb"><i class="el-icon-headset"></i></div>
              <div class="yt-side-hero__meta">
                <div class="yt-side-hero__label">저작물: {{ formatType(asset.type) || '음원' }}</div>
                <div class="yt-side-hero__title">{{ title || '제목 없음' }}</div>
                <div class="yt-side-hero__id">{{ asset.id }}</div>
              </div>
            </div>

            <nav class="yt-sidenav">
              <button
                v-for="it in sidebarPrimary"
                :key="it.key"
                type="button"
                class="yt-sidenav__item"
                :class="{ 'is-active': section === it.key, 'is-disabled': it.disabled }"
                :disabled="it.disabled"
                @click="!it.disabled && (section = it.key)"
              >
                <i :class="it.icon"></i>
                <span class="yt-sidenav__label">{{ it.label }}</span>
                <span
                  v-if="it.count"
                  class="yt-sidenav__count"
                  :class="{ 'yt-sidenav__count--alert': it.key === 'issues' }"
                >{{ it.count }}</span>
              </button>

            </nav>
          </aside>

          <!-- Main content -->
          <main class="yt-main">
            <template v-if="section === 'metadata'">
              <!-- Global meta strip -->
              <section class="yt-section">
                <div class="yt-section-head">
                  <h3 class="yt-section-title">전체</h3>
                  <button class="yt-btn yt-btn--ghost" type="button" disabled>
                    메타데이터 비교
                  </button>
                </div>
                <div class="yt-global-grid">
                  <div class="yt-meta">
                    <div class="yt-meta__label">저작물 ID</div>
                    <div class="yt-meta__value yt-mono">{{ asset.id }}</div>
                  </div>
                  <div class="yt-meta">
                    <div class="yt-meta__label">생성일</div>
                    <div class="yt-meta__value">{{ formatDate(asset.timeCreated || md.timeCreated) || '—' }}</div>
                  </div>
                  <div class="yt-meta">
                    <div class="yt-meta__label">업데이트 날짜</div>
                    <div class="yt-meta__value">{{ formatDate(asset.timeUpdated || md.timeUpdated) || '—' }}</div>
                  </div>
                  <div class="yt-meta">
                    <div class="yt-meta__label">ISRC{{ isrcList.length > 1 ? ` (${isrcList.length})` : '' }}</div>
                    <div class="yt-meta__value yt-mono">{{ isrcList.join(', ') || '—' }}</div>
                  </div>
                  <div class="yt-meta">
                    <div class="yt-meta__label">
                      소유권
                      <span v-if="ownershipConflict" class="yt-conflict-pill">
                        <span class="yt-dot"></span> 충돌
                      </span>
                    </div>
                    <div class="yt-meta__value yt-ownership-tags">
                      <span v-for="o in ownershipList" :key="o" class="yt-tag">{{ o }}</span>
                      <span v-if="!ownershipList.length" class="yt-muted">—</span>
                    </div>
                  </div>
                  <div class="yt-meta">
                    <div class="yt-meta__label">문제</div>
                    <div class="yt-meta__value">
                      <span v-if="issuesCount" class="yt-issue-pill">{{ issuesCount }}</span>
                      <span v-else class="yt-muted">—</span>
                    </div>
                  </div>
                  <div class="yt-meta">
                    <div class="yt-meta__label">라이선스 부여 가능성</div>
                    <div class="yt-meta__value">
                      <span class="yt-status" :class="`yt-status--${licensableIcon.color}`" :title="licensableIcon.tip">
                        <i :class="licensableIcon.cls"></i>
                        <span class="yt-status__text">{{ licensableIcon.tip }}</span>
                      </span>
                    </div>
                  </div>
                  <div class="yt-meta">
                    <div class="yt-meta__label">수익 공유</div>
                    <div class="yt-meta__value">
                      <span class="yt-status" :class="`yt-status--${revenueSharingIcon.color}`" :title="revenueSharingIcon.tip">
                        <i :class="revenueSharingIcon.cls"></i>
                        <span class="yt-status__text">{{ revenueSharingIcon.tip }}</span>
                      </span>
                    </div>
                  </div>
                  <div v-if="aliasIds.length" class="yt-meta yt-meta--full">
                    <div class="yt-meta__label">별칭 ID ({{ aliasIds.length }})</div>
                    <div class="yt-meta__value yt-alias-list">
                      <router-link
                        v-for="aid in aliasIds"
                        :key="aid"
                        :to="`/youtube-cid/asset/${aid}`"
                        class="yt-alias-chip yt-mono"
                      >{{ aid }}</router-link>
                    </div>
                  </div>
                </div>
              </section>

              <!-- Your metadata -->
              <section class="yt-section">
                <h3 class="yt-section-title">내 메타데이터</h3>
                <div class="yt-yourmeta">
                  <div class="yt-yourmeta__title">
                    <span class="yt-yourmeta__title-text">{{ title || '제목 없음' }}</span>
                    <span class="yt-yourmeta__type"><i class="el-icon-headset"></i> {{ formatType(asset.type) || '음원' }}</span>
                  </div>
                  <div class="yt-yourmeta__stats">
                    <div class="yt-stat">
                      <div class="yt-stat__label">일일 유효 조회수</div>
                      <div class="yt-stat__value">—</div>
                    </div>
                    <div class="yt-stat">
                      <div class="yt-stat__label">정책</div>
                      <div class="yt-stat__value">
                        <span class="yt-policy-icon">$</span>
                        <span>맞춤 정책</span>
                      </div>
                    </div>
                  </div>
                  <div class="yt-yourmeta__row">
                    <div class="yt-meta">
                      <div class="yt-meta__label">맞춤 ID</div>
                      <div class="yt-meta__value yt-mono">{{ asset.customId || '—' }}</div>
                    </div>
                    <div class="yt-meta">
                      <div class="yt-meta__label">저작물 라벨</div>
                      <div class="yt-meta__value yt-muted">—</div>
                    </div>
                  </div>
                </div>
              </section>

              <!-- Your shares (form) + History panel -->
              <section class="yt-section yt-shares-section">
                <h3 class="yt-section-title">내 공유 항목</h3>
                <div class="yt-shares">
                  <div class="yt-shares__form">
                    <div class="yt-field-grid">
                      <div class="yt-field">
                        <label>노래 <span class="yt-req">(필수 항목)</span></label>
                        <input type="text" :value="md.title || md.songTitle || ''" readonly />
                      </div>
                      <div class="yt-field">
                        <label>맞춤 ID</label>
                        <input type="text" :value="asset.customId || ''" readonly />
                      </div>
                      <div class="yt-field">
                        <label>아티스트 <span class="yt-req">(필수 항목)</span></label>
                        <div class="yt-field__input yt-field__chips">
                          <span v-for="a in artistList" :key="a" class="yt-tag yt-tag--input">
                            {{ a }} <i class="el-icon-close"></i>
                          </span>
                          <span v-if="!artistList.length" class="yt-muted">—</span>
                        </div>
                        <div class="yt-field__hint">각 이름 입력 후 Enter 키를 누르세요</div>
                      </div>
                      <div class="yt-field">
                        <label>GRID</label>
                        <input type="text" :value="md.grid || ''" placeholder="GRID 추가" readonly />
                      </div>
                      <div class="yt-field">
                        <label>음반사</label>
                        <input type="text" :value="md.label || ''" readonly />
                      </div>
                      <div class="yt-field">
                        <label>ISRC</label>
                        <input type="text" :value="md.isrc || ''" readonly />
                      </div>
                      <div class="yt-field">
                        <label>장르</label>
                        <div class="yt-field__input yt-field__chips">
                          <span v-for="g in genreList" :key="g" class="yt-tag yt-tag--input">
                            {{ g }} <i class="el-icon-close"></i>
                          </span>
                          <span v-if="!genreList.length" class="yt-muted">—</span>
                        </div>
                        <div class="yt-field__hint">쉼표로 구분된 값을 입력하세요</div>
                      </div>
                      <div class="yt-field">
                        <label>UPC</label>
                        <input type="text" :value="md.upc || ''" readonly />
                      </div>
                      <div class="yt-field yt-field--full">
                        <label>메모</label>
                        <textarea rows="3" :value="md.description || ''" placeholder="메모 추가" readonly></textarea>
                      </div>
                      <div class="yt-field">
                        <label>앨범</label>
                        <input type="text" :value="md.album || ''" readonly />
                      </div>
                      <div class="yt-field">
                        <label>출시일</label>
                        <input type="text" :value="formatDate(md.releaseDate)" readonly />
                      </div>
                      <div class="yt-field yt-field--full">
                        <label>저작물 라벨</label>
                        <input type="text" value="" placeholder="저작물 라벨 추가" readonly />
                        <div class="yt-field__hint yt-field__hint--right">0 / 30</div>
                      </div>
                    </div>
                  </div>

                  <aside class="yt-history">
                    <div class="yt-history__head">
                      <h4>기록 ({{ historyEntries.length }})</h4>
                    </div>
                    <div class="yt-history__list">
                      <div
                        v-for="(h, i) in historyEntries"
                        :key="i"
                        class="yt-history__item"
                        :class="{ 'is-current': i === 0 }"
                      >
                        <span class="yt-history__pill">공유 {{ historyEntries.length - i }}</span>
                        <div class="yt-history__when">
                          <div class="yt-history__date">{{ h.date }}</div>
                          <div v-if="h.time" class="yt-history__time">{{ h.time }}</div>
                        </div>
                        <div class="yt-history__src">
                          <div v-if="h.sourceLabel" class="yt-history__src-label">{{ h.sourceLabel }}</div>
                          <div v-if="h.owner" class="yt-history__src-owner" :title="h.ownerId || h.owner">{{ h.owner }}</div>
                        </div>
                        <i class="el-icon-box yt-history__asset-icon" title="저작물"></i>
                      </div>
                      <div v-if="!historyEntries.length" class="yt-history__empty">변경 기록 없음</div>
                    </div>
                    <div class="yt-history__foot">
                      <button class="yt-history__revert" type="button" disabled>되돌리기</button>
                    </div>
                  </aside>
                </div>
              </section>
            </template>

            <template v-else-if="section === 'issues'">
              <section class="yt-section">
                <h3 class="yt-section-title">문제</h3>
                <div v-if="issuesCount" class="yt-issue-list">
                  <div v-for="(iss, i) in derivedIssues" :key="i" class="yt-issue-row">
                    <i class="el-icon-warning yt-icon-warn"></i>
                    <span class="yt-issue-row__type">{{ iss.type }}</span>
                    <span class="yt-issue-row__src">· {{ iss.source }}</span>
                  </div>
                </div>
                <div v-else class="yt-state">문제 없음</div>
              </section>
            </template>

            <template v-else-if="section === 'ownership'">
              <AssetOwnershipTab
                :ownership="ownership"
                :loading="sectionLoading.ownership"
              ></AssetOwnershipTab>
            </template>
            <template v-else-if="section === 'history'">
              <AssetHistoryTab
                :items="metadataHistory"
                :loading="sectionLoading.history"
              ></AssetHistoryTab>
            </template>
            <template v-else-if="section === 'references'">
              <AssetReferencesTab
                :asset-id="asset.id"
                :items="referenceItems"
                :loading="sectionLoading.references"
                @deactivate="onDeactivateReference"
                @activate="onActivateReference"
              ></AssetReferencesTab>
            </template>
            <template v-else-if="section === 'match-policy'">
              <AssetMatchPolicyTab
                :asset-id="asset.id"
                :match-policy="matchPolicy"
                :loading="sectionLoading['match-policy']"
                @updated="onMatchPolicyUpdated"
              ></AssetMatchPolicyTab>
            </template>
            <template v-else-if="section === 'claims'">
              <AssetClaimsTab
                :items="claimItems"
                :loading="sectionLoading.claims"
                :loading-more="claimsLoadingMore"
                :has-more="claimsHasMore"
                :total="claimsTotal"
                :video-id-filter="claimsVideoIdFilter"
                @load-more="loadMoreClaims"
                @update:videoIdFilter="onClaimsVideoIdFilter"
              ></AssetClaimsTab>
            </template>
            <template v-else-if="section === 'analytics'">
              <div class="yt-analytics-empty">
                <div class="yt-analytics-empty__icon"><i class="el-icon-data-line"></i></div>
                <p class="yt-analytics-empty__lead">분석 데이터는 YouTube CMS에서 확인할 수 있습니다.</p>
                <p class="yt-analytics-empty__hint">조회수, 시청 시간, 수익 등 자세한 통계를 보려면 아래 버튼을 클릭하세요.</p>
                <a
                  :href="cmsAnalyticsUrl"
                  target="_blank"
                  rel="noopener"
                  class="yt-analytics-empty__cta"
                >
                  <i class="el-icon-data-line"></i>
                  <span>YouTube CMS에서 분석 열기</span>
                  <span aria-hidden="true">↗</span>
                </a>
              </div>
            </template>
          </main>
        </div>
      </vx-card>
    </div>

    <ConfirmActionDialog
      :visible.sync="refDialogOpen"
      :title="refAction === 'deactivate' ? '참조 비활성화' : '참조 활성화'"
      :confirm-token="pendingRef ? pendingRef.id : ''"
      :confirm-label="refAction === 'deactivate' ? '참조 비활성화' : '참조 활성화'"
      :dangerous="refAction === 'deactivate'"
      :submitting="refSubmitting"
      @confirm="onConfirmRefAction"
    >
      <template #subject v-if="pendingRef">
        참조 <strong>{{ pendingRef.id }}</strong> · 저작물 <strong>{{ asset && asset.id }}</strong>
      </template>
      <template #diff v-if="pendingRef">
        <div style="font-family:ui-monospace,'SF Mono',Menlo,Consolas,monospace;font-size:12px;line-height:1.6;">
          <div style="color:#EA5455;">− status: {{ pendingRef.status }}</div>
          <div style="color:#28C76F;">+ status: {{ refAction === 'deactivate' ? 'inactive' : 'active' }}</div>
        </div>
      </template>
    </ConfirmActionDialog>
  </div>
</template>

<script>
import { YoutubeCidApi, youtubeCmsAssetUrl, youtubeCmsAssetAnalyticsUrl, resolveContentOwnerName } from './api.js'
import ConfirmActionDialog from './components/ConfirmActionDialog.vue'
import AssetOwnershipTab from './tabs/AssetOwnershipTab.vue'
import AssetReferencesTab from './tabs/AssetReferencesTab.vue'
import AssetMatchPolicyTab from './tabs/AssetMatchPolicyTab.vue'
import AssetClaimsTab from './tabs/AssetClaimsTab.vue'
import AssetHistoryTab from './tabs/AssetHistoryTab.vue'
import { toLocalDate, toLocalTime } from './dateFormat.js'

function fmtIso(iso) {
  return toLocalDate(iso)
}
function fmtTime(iso) {
  return toLocalTime(iso)
}
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
function fmtDateLong(iso) {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`
  } catch (e) { return '' }
}
const SOURCE_TYPE_LABELS = {
  contentDelivery: 'Content delivery',
  content_delivery: 'Content delivery',
  ingestion: 'Content delivery',
  assetIngestion: 'Content delivery',
  cms: 'CMS',
  api: 'API',
  partner: 'Partner',
  user: 'User',
  system: 'System',
}
function sourceTypeLabel(t) {
  if (!t) return ''
  if (SOURCE_TYPE_LABELS[t]) return SOURCE_TYPE_LABELS[t]
  return String(t).replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, (c) => c.toUpperCase())
}

export default {
  name: 'YoutubeCidAssetDetail',
  components: {
    ConfirmActionDialog,
    AssetOwnershipTab,
    AssetReferencesTab,
    AssetMatchPolicyTab,
    AssetClaimsTab,
    AssetHistoryTab,
  },
  data() {
    return {
      loading: false,
      asset: null,
      cache: {
        ownership: null,
        matchPolicy: null,
        history: null,
        references: null,
        claims: null,
        shares: null,
      },
      sectionLoading: {
        ownership: false,
        'match-policy': false,
        history: false,
        references: false,
        claims: false,
        shares: false,
      },
      pageTab: 'overview',
      section: 'metadata',
      refDialogOpen: false,
      refSubmitting: false,
      pendingRef: null,
      refAction: 'deactivate',
      claimsLoadingMore: false,
      claimsVideoIdFilter: '',
    }
  },
  computed: {
    cmsUrl() {
      return youtubeCmsAssetUrl(this.$route.params.id)
    },
    cmsAnalyticsUrl() {
      return youtubeCmsAssetAnalyticsUrl(this.$route.params.id)
    },
    md() {
      if (!this.asset) return {}
      const nested = this.asset.metadataMine || this.asset.metadata || this.asset.metadataEffective || {}
      // Merge top-level snippet fields as fallbacks (search endpoint returns flat shape)
      return {
        title: this.asset.title,
        isrc: this.asset.isrc,
        isrcs: this.asset.isrcs,
        customId: this.asset.customId,
        timeCreated: this.asset.timeCreated,
        timeUpdated: this.asset.timeUpdated,
        ...nested,
      }
    },
    title() {
      return this.md.title || this.md.episodeTitle || this.md.movieTitle || this.md.songTitle || ''
    },
    isMerged() {
      if (!this.asset) return false
      if (this.asset.isMerged || this.asset.merged) return true
      return Array.isArray(this.asset.aliasId) && this.asset.aliasId.length > 0
    },
    aliasIds() {
      const a = this.asset && this.asset.aliasId
      return Array.isArray(a) ? a : []
    },
    licensability() {
      return (this.asset && this.asset.licensability) || null
    },
    licensableIcon() {
      const l = this.licensability
      if (!l) return { cls: 'el-icon-warning-outline', color: 'warn', tip: '데이터 없음' }
      if (l.licensable) return { cls: 'el-icon-success', color: 'ok', tip: '라이선스 부여 가능' }
      const reason = Array.isArray(l.unlicensableReason)
        ? l.unlicensableReason.join(', ').replace(/_/g, ' ')
        : (l.licensabilityState || '라이선스 부여 불가').replace(/_/g, ' ')
      return { cls: 'el-icon-warning', color: 'warn', tip: reason }
    },
    revenueSharing() {
      return (this.asset && this.asset.nWayRevenueSharing) || null
    },
    revenueSharingIcon() {
      const r = this.revenueSharing
      if (!r) return { cls: 'el-icon-warning-outline', color: 'warn', tip: '데이터 없음' }
      const status = String(r.status || '').toLowerCase()
      if (status.includes('unrestricted')) {
        const count = Array.isArray(r.eligibleTerritories) ? r.eligibleTerritories.length : 0
        return { cls: 'el-icon-share', color: 'ok', tip: count ? `제한 없음 · ${count}개 지역` : '제한 없음' }
      }
      return { cls: 'el-icon-warning', color: 'warn', tip: status.replace(/_/g, ' ') || '제한됨' }
    },
    isrcList() {
      const list = []
      if (this.md.isrc) list.push(this.md.isrc)
      const sources = [this.md.isrcs, this.asset && this.asset.isrcs, this.asset && this.asset.alternativeIsrcs]
      for (const src of sources) {
        if (Array.isArray(src)) src.forEach((v) => v && !list.includes(v) && list.push(v))
      }
      return list
    },
    artistList() {
      const a = this.md.artist || this.md.artists
      if (!a) return []
      if (Array.isArray(a)) return a
      return String(a).split(/[,;]/).map((s) => s.trim()).filter(Boolean)
    },
    genreList() {
      const g = this.md.genre || this.md.genres
      if (!g) return []
      if (Array.isArray(g)) return g
      return String(g).split(/[,;]/).map((s) => s.trim()).filter(Boolean)
    },
    ownership() {
      if (this.cache.ownership) return this.cache.ownership
      if (this.asset && this.asset.ownershipEffective) return this.asset.ownershipEffective
      if (this.asset && this.asset.ownership) return this.asset.ownership
      return null
    },
    matchPolicy() {
      if (this.cache.matchPolicy) return this.cache.matchPolicy
      if (this.asset && this.asset.matchPolicyEffective) return this.asset.matchPolicyEffective
      if (this.asset && this.asset.matchPolicy) return this.asset.matchPolicy
      return null
    },
    metadataHistory() {
      const h = this.cache.history
      const items = (h && Array.isArray(h.items)) ? h.items : []
      // Newest first; falls back to original order when timestamps are missing/equal.
      return [...items].sort((a, b) => {
        const ta = (a && (a.timeProvided || a.time)) || ''
        const tb = (b && (b.timeProvided || b.time)) || ''
        return tb.localeCompare(ta)
      })
    },
    referenceItems() {
      const r = this.cache.references
      return (r && Array.isArray(r.items)) ? r.items : []
    },
    claimItems() {
      const c = this.cache.claims
      return (c && Array.isArray(c.items)) ? c.items : []
    },
    shares() {
      const s = this.cache.shares
      return (s && Array.isArray(s.items)) ? s.items : []
    },
    referencesTotal() {
      const r = this.cache.references
      if (!r || !Array.isArray(r.items)) return null
      return r.items.length
    },
    claimsTotal() {
      const c = this.cache.claims
      if (!c) return null
      const total = c.pageInfo && Number(c.pageInfo.totalResults)
      if (Number.isFinite(total)) return total
      return Array.isArray(c.items) ? c.items.length : null
    },
    claimsHasMore() {
      const c = this.cache.claims
      return !!(c && c.nextPageToken)
    },
    ownershipList() {
      const owners = this.ownership && this.ownership.owners
      if (!Array.isArray(owners)) return []
      return owners.map((o) => o.name || o.ownerName || o.contentOwnerId).filter(Boolean)
    },
    ownershipConflict() {
      return !!(this.ownership && this.ownership.conflict)
    },
    derivedIssues() {
      if (!this.asset) return []
      const list = []
      if (Array.isArray(this.asset.issues)) {
        this.asset.issues.forEach((iss) => list.push({ source: 'asset', type: iss.type || iss.subject || String(iss) }))
      }
      const lic = this.asset.licensability
      if (lic && lic.licensable === false && Array.isArray(lic.unlicensableReason)) {
        lic.unlicensableReason.forEach((r) => list.push({ source: 'licensability', type: String(r).replace(/_/g, ' ') }))
      }
      return list
    },
    issuesCount() {
      return this.derivedIssues.length
    },
    sharesCount() {
      return this.shares.length
    },
    historyEntries() {
      if (!Array.isArray(this.metadataHistory) || !this.metadataHistory.length) return []
      const sorted = [...this.metadataHistory].sort((a, b) => {
        const ta = a.timeProvided || a.time || ''
        const tb = b.timeProvided || b.time || ''
        return tb.localeCompare(ta)
      })
      const pickStr = (v) => (typeof v === 'string' && v) ? v : ''
      return sorted.map((h) => {
        const t = h.timeProvided || h.time || ''
        const src = (h.source && typeof h.source === 'object') ? h.source : {}
        const orig = (h.origination && typeof h.origination === 'object') ? h.origination : {}
        const sourceType =
          pickStr(h.source) ||
          pickStr(src.type) ||
          pickStr(src.source) ||
          pickStr(h.origination) ||
          pickStr(orig.source) ||
          pickStr(orig.type) ||
          ''
        const owner =
          pickStr(src.contentOwnerId) ||
          pickStr(src.owner) ||
          pickStr(orig.contentOwnerId) ||
          pickStr(orig.owner) ||
          pickStr(h.contentOwnerId) ||
          ''
        return {
          date: fmtDateLong(t),
          time: fmtTime(t),
          sourceLabel: sourceTypeLabel(sourceType),
          owner: resolveContentOwnerName(owner),
          ownerId: owner,
        }
      })
    },
    sidebarPrimary() {
      return [
        { key: 'metadata', label: '메타데이터', icon: 'el-icon-edit-outline' },
        { key: 'issues', label: '문제', icon: 'el-icon-warning-outline', count: this.issuesCount || null },
        { key: 'ownership', label: '소유권 및 정책', icon: 'el-icon-collection-tag' },
        { key: 'history', label: '기록', icon: 'el-icon-time' },
        { key: 'references', label: '참조', icon: 'el-icon-copy-document', count: this.referencesTotal || null },
        { key: 'embedded', label: '퍼간 위치', icon: 'el-icon-folder', count: this.claimsTotal || null, disabled: true },
        { key: 'analytics', label: '분석', icon: 'el-icon-data-line' },
        { key: 'claims', label: '소유권이 주장된 동영상', icon: 'el-icon-video-camera', count: this.claimsTotal || null },
        { key: 'match-policy', label: '수익 창출', icon: 'el-icon-money' },
      ]
    },
  },
  watch: {
    '$route.params.id': { immediate: true, handler() { this.load() } },
    section(val) { this.ensureSection(val) },
    pageTab(val) {
      if (val === 'shares' && this.asset && !this.cache.shares) this.loadShares()
    },
  },
  methods: {
    async load() {
      const id = this.$route.params.id
      if (!id) return
      this.loading = true
      this.asset = null
      this.cache = { ownership: null, matchPolicy: null, history: null, references: null, claims: null, shares: null }
      try {
        this.asset = await YoutubeCidApi.getAsset(id)
        await this.ensureSection(this.section)
      } finally {
        this.loading = false
      }
    },
    async ensureSection(section) {
      if (!this.asset || !this.asset.id) return
      const id = this.asset.id
      if (section === 'metadata') {
        if (this.cache.history) return
        await this.runSectionFetch('history', 'history', () => YoutubeCidApi.getMetadataHistory(id))
      } else if (section === 'ownership') {
        if (this.cache.ownership || this.asset.ownershipEffective || this.asset.ownership) return
        await this.runSectionFetch('ownership', 'ownership', () => YoutubeCidApi.getOwnership(id))
      } else if (section === 'match-policy') {
        if (this.cache.matchPolicy || this.asset.matchPolicyEffective || this.asset.matchPolicy) return
        await this.runSectionFetch('match-policy', 'matchPolicy', () => YoutubeCidApi.getMatchPolicy(id))
      } else if (section === 'history') {
        if (this.cache.history) return
        await this.runSectionFetch('history', 'history', () => YoutubeCidApi.getMetadataHistory(id))
      } else if (section === 'references') {
        if (this.cache.references) return
        await this.runSectionFetch('references', 'references', () => YoutubeCidApi.listReferences({ assetId: id }))
      } else if (section === 'claims') {
        if (this.cache.claims) return
        await this.runSectionFetch('claims', 'claims', () => YoutubeCidApi.searchClaims(this.buildClaimsParams()))
      }
    },
    buildClaimsParams() {
      const params = { assetId: this.asset && this.asset.id }
      const v = (this.claimsVideoIdFilter || '').trim()
      if (v) params.videoId = v
      return params
    },
    async onClaimsVideoIdFilter(value) {
      const next = (value || '').trim()
      if (next === this.claimsVideoIdFilter) return
      this.claimsVideoIdFilter = next
      if (!this.asset || !this.asset.id) return
      this.cache.claims = null
      await this.runSectionFetch('claims', 'claims', () => YoutubeCidApi.searchClaims(this.buildClaimsParams()))
    },
    async loadMoreClaims() {
      const c = this.cache.claims
      if (!c || !c.nextPageToken || this.claimsLoadingMore) return
      if (!this.asset || !this.asset.id) return
      this.claimsLoadingMore = true
      try {
        const next = await YoutubeCidApi.searchClaims({
          ...this.buildClaimsParams(),
          pageToken: c.nextPageToken,
        })
        const prevItems = Array.isArray(c.items) ? c.items : []
        const nextItems = (next && Array.isArray(next.items)) ? next.items : []
        this.cache.claims = {
          ...c,
          ...next,
          items: [...prevItems, ...nextItems],
          nextPageToken: (next && next.nextPageToken) || '',
        }
      } catch (e) {
        // surface paging error silently; existing items remain visible
      } finally {
        this.claimsLoadingMore = false
      }
    },
    async runSectionFetch(loadingKey, cacheKey, fetcher) {
      this.$set(this.sectionLoading, loadingKey, true)
      try {
        this.cache[cacheKey] = await fetcher()
      } catch (e) {
        this.cache[cacheKey] = null
      } finally {
        this.$set(this.sectionLoading, loadingKey, false)
      }
    },
    async loadShares() {
      if (!this.asset || !this.asset.id) return
      await this.runSectionFetch('shares', 'shares', () => YoutubeCidApi.getAssetShares(this.asset.id))
    },
    async onMatchPolicyUpdated() {
      if (!this.asset || !this.asset.id) return
      this.cache.matchPolicy = null
      await this.runSectionFetch('match-policy', 'matchPolicy', () => YoutubeCidApi.getMatchPolicy(this.asset.id))
    },
    formatType(t) {
      if (!t) return ''
      return String(t).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    },
    formatDate(iso) {
      if (!iso) return ''
      if (typeof iso === 'object' && iso !== null) {
        const { year, month, day } = iso
        if (year) {
          const m = month ? String(month).padStart(2, '0') : null
          const d = day ? String(day).padStart(2, '0') : null
          return [year, m, d].filter(Boolean).join('-')
        }
        return ''
      }
      const formatted = toLocalDate(iso)
      return formatted || String(iso)
    },
    onDeactivateReference(r) {
      this.pendingRef = r
      this.refAction = 'deactivate'
      this.refDialogOpen = true
    },
    onActivateReference(r) {
      this.pendingRef = r
      this.refAction = 'activate'
      this.refDialogOpen = true
    },
    async onConfirmRefAction() {
      if (!this.pendingRef) return
      this.refSubmitting = true
      try {
        const status = this.refAction === 'deactivate' ? 'inactive' : 'active'
        await YoutubeCidApi.patchReference(this.pendingRef.id, { status })
        this.refDialogOpen = false
        this.pendingRef = null
        this.cache.references = null
        await this.ensureSection('references')
      } finally {
        this.refSubmitting = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
$yt-border: rgba(0, 0, 0, 0.08);
$yt-border-strong: rgba(0, 0, 0, 0.14);
$yt-text: #0f0f0f;
$yt-text-muted: rgba(15, 15, 15, 0.6);
$yt-text-faint: rgba(15, 15, 15, 0.42);
$yt-bg-hover: rgba(15, 15, 15, 0.04);
$yt-bg-sidebar: #f9f9f9;
$yt-accent: #065fd4;
$yt-warn: #ed6c02;
$yt-conflict: #d93025;

.ytcid-detail {
  font-family: 'Roboto', 'YouTube Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: $yt-text;
}
.ytcid-detail-card ::v-deep .vx-card__body { padding: 0; }

.yt-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid $yt-border;
}
.yt-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: $yt-text-muted;
  text-decoration: none;
  font-size: 13px;
  padding: 4px 8px;
  border-radius: 4px;
  &:hover { color: $yt-text; background: $yt-bg-hover; }
}
.yt-topbar__right {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.yt-link-out {
  font-size: 12px;
  color: $yt-accent;
  text-decoration: none;
  margin-right: 8px;
  &:hover { text-decoration: underline; }
}

.yt-btn {
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 14px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 4px;
  border: 1px solid transparent;
  cursor: pointer;
  background: #fff;
  transition: background 100ms ease;
  &:disabled { opacity: 0.45; cursor: not-allowed; }
  &--ghost {
    color: $yt-text-muted;
    border-color: $yt-border-strong;
    &:hover:not(:disabled) { background: $yt-bg-hover; color: $yt-text; }
  }
  &--primary {
    background: $yt-accent;
    color: #fff;
    &:hover:not(:disabled) { background: #054ba8; }
  }
}

.yt-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: rgba(6, 95, 212, 0.06);
  color: $yt-text;
  font-size: 13px;
  border-bottom: 1px solid $yt-border;
  i { color: $yt-accent; }
}

.yt-page-tabs {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 16px;
  border-bottom: 1px solid $yt-border;
}
.yt-tab {
  background: transparent;
  border: 0;
  height: 48px;
  padding: 0 16px;
  font-size: 14px;
  font-weight: 500;
  color: $yt-text-muted;
  cursor: pointer;
  position: relative;
  &:hover { color: $yt-text; }
  &.is-active {
    color: $yt-text;
    font-weight: 600;
    &::after {
      content: '';
      position: absolute;
      left: 12px; right: 12px; bottom: -1px;
      height: 2px;
      background: $yt-text;
      border-radius: 2px 2px 0 0;
    }
  }
}

.yt-state {
  padding: 64px 16px;
  text-align: center;
  color: $yt-text-faint;
  font-size: 14px;
}

.yt-analytics-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 80px 24px;
  gap: 6px;
}
.yt-analytics-empty__icon {
  width: 56px;
  height: 56px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(6, 95, 212, 0.08);
  color: $yt-accent;
  font-size: 24px;
  margin-bottom: 14px;
}
.yt-analytics-empty__lead {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: $yt-text;
}
.yt-analytics-empty__hint {
  margin: 0 0 18px;
  font-size: 13px;
  color: $yt-text-muted;
  max-width: 420px;
  line-height: 1.55;
}
.yt-analytics-empty__cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 18px;
  border-radius: 18px;
  background: $yt-accent;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  transition: background 120ms ease;
  &:hover { background: darken(#065fd4, 6%); }
  i { font-size: 14px; }
  span:last-child { font-size: 12px; opacity: 0.85; margin-left: 2px; }
}

.yt-body {
  display: grid;
  grid-template-columns: 260px 1fr;
  min-height: 480px;
}

.yt-sidebar {
  border-right: 1px solid $yt-border;
  background: $yt-bg-sidebar;
}
.yt-side-hero {
  display: flex;
  gap: 12px;
  padding: 20px 16px 16px;
  border-bottom: 1px solid $yt-border;
}
.yt-side-thumb {
  width: 56px;
  height: 56px;
  border-radius: 4px;
  background: #fff;
  border: 1px solid $yt-border;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: $yt-accent;
  font-size: 22px;
  flex-shrink: 0;
}
.yt-side-hero__meta { min-width: 0; }
.yt-side-hero__label {
  font-size: 12px;
  color: $yt-text-muted;
  margin-bottom: 4px;
}
.yt-side-hero__title {
  font-size: 14px;
  font-weight: 500;
  color: $yt-text;
  line-height: 1.3;
  word-break: break-word;
}
.yt-side-hero__id {
  font-size: 11px;
  color: $yt-text-faint;
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  margin-top: 4px;
  word-break: break-all;
}

.yt-sidenav {
  display: flex;
  flex-direction: column;
  padding: 8px 0;
}
.yt-sidenav__item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  height: 40px;
  padding: 0 16px;
  background: transparent;
  border: 0;
  border-left: 3px solid transparent;
  color: $yt-text-muted;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  transition: background 100ms ease, color 100ms ease;
  i { font-size: 16px; width: 16px; flex-shrink: 0; }
  &:hover:not(.is-disabled) { background: $yt-bg-hover; color: $yt-text; }
  &.is-active {
    background: rgba(6, 95, 212, 0.08);
    color: $yt-accent;
    font-weight: 600;
    border-left-color: $yt-accent;
  }
  &.is-disabled { opacity: 0.5; cursor: not-allowed; }
}
.yt-sidenav__label { flex: 1; }
.yt-sidenav__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  font-size: 11px;
  background: rgba(15, 15, 15, 0.08);
  color: $yt-text-muted;
  font-weight: 600;
  &--alert { background: $yt-warn; color: #fff; }
}
.yt-main {
  padding: 0;
  background: #fff;
  min-width: 0;
}

.yt-section {
  padding: 24px 32px;
  border-bottom: 1px solid $yt-border;
  &:last-child { border-bottom: 0; }
}
.yt-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.yt-section-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 16px;
  color: $yt-text;
}
.yt-section-head .yt-section-title { margin-bottom: 0; }

.yt-global-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px 24px;
}
.yt-meta__label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: $yt-text-faint;
  margin-bottom: 4px;
}
.yt-meta__value {
  font-size: 13px;
  color: $yt-text;
  word-break: break-word;
}
.yt-mono {
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 12px;
}
.yt-muted { color: $yt-text-faint; }

.yt-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 12px;
  background: rgba(15, 15, 15, 0.06);
  color: $yt-text;
  margin-right: 6px;
  &--input {
    background: rgba(6, 95, 212, 0.08);
    color: $yt-accent;
    i { font-size: 11px; cursor: pointer; opacity: 0.6; &:hover { opacity: 1; } }
  }
}
.yt-ownership-tags { display: inline-flex; flex-wrap: wrap; gap: 4px; }

.yt-conflict-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: $yt-conflict;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
}
.yt-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: $yt-conflict;
}
.yt-issue-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 7px;
  border-radius: 10px;
  background: $yt-warn;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
}
.yt-icon-cell { display: inline-flex; align-items: center; }
.yt-icon-warn { color: $yt-warn; font-size: 18px; }

.yt-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  text-transform: capitalize;
  i { font-size: 16px; }
  &--ok { color: #2e7d32; }
  &--warn { color: $yt-warn; }
}
.yt-status__text { color: $yt-text-muted; }

.yt-meta--full { grid-column: 1 / -1; }
.yt-alias-list { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 2px; }
.yt-alias-chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 12px;
  border: 1px solid $yt-border;
  background: #fff;
  color: $yt-text;
  text-decoration: none;
  font-size: 11px;
  &:hover { border-color: $yt-accent; color: $yt-accent; }
}

.yt-banner__aliases { color: $yt-text-muted; font-size: 12px; }

.yt-issue-row__type { flex: 1; font-weight: 500; }
.yt-issue-row__src { color: $yt-text-faint; font-size: 12px; text-transform: capitalize; }

.yt-yourmeta {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.yt-yourmeta__title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 15px;
  font-weight: 500;
}
.yt-yourmeta__title-text { color: $yt-text; }
.yt-yourmeta__type {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: $yt-text-muted;
  font-size: 13px;
  font-weight: 400;
  i { color: $yt-accent; }
}
.yt-yourmeta__stats {
  display: flex;
  gap: 32px;
}
.yt-stat__label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: $yt-text-faint;
  margin-bottom: 4px;
}
.yt-stat__value {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
}
.yt-policy-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px; height: 20px;
  border-radius: 50%;
  background: rgba(46, 125, 50, 0.12);
  color: #2e7d32;
  font-size: 12px;
  font-weight: 600;
}
.yt-yourmeta__row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px 24px;
}

.yt-shares {
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 24px;
  align-items: flex-start;
}
.yt-field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px 16px;
}
.yt-field {
  display: flex;
  flex-direction: column;
  position: relative;
  &--full { grid-column: 1 / -1; }
  label {
    font-size: 11px;
    font-weight: 500;
    color: $yt-text-muted;
    margin-bottom: 4px;
    .yt-req { color: $yt-text-faint; font-weight: 400; }
  }
  input, textarea, .yt-field__input {
    border: 1px solid $yt-border-strong;
    border-radius: 4px;
    padding: 8px 12px;
    font-size: 13px;
    font-family: inherit;
    color: $yt-text;
    background: #fff;
    width: 100%;
    transition: border-color 120ms ease, box-shadow 120ms ease;
    min-height: 36px;
    &:focus {
      outline: 0;
      border-color: $yt-accent;
      box-shadow: inset 0 0 0 1px $yt-accent;
    }
    &[readonly] {
      background: rgba(15, 15, 15, 0.02);
      cursor: default;
    }
  }
  textarea { resize: vertical; min-height: 64px; line-height: 1.5; }
  &__chips {
    display: flex; align-items: center; flex-wrap: wrap; gap: 4px;
    background: rgba(15, 15, 15, 0.02);
    padding: 6px 10px;
  }
  &__hint {
    font-size: 11px;
    color: $yt-text-faint;
    margin-top: 4px;
    &--right { text-align: right; }
  }
}

.yt-history {
  border: 1px solid $yt-border;
  border-radius: 8px;
  background: #fff;
  position: sticky;
  top: 16px;
  overflow: hidden;
}
.yt-history__head {
  padding: 16px 20px;
  border-bottom: 1px solid $yt-border;
  h4 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: $yt-text;
  }
}
.yt-history__list {
  display: flex;
  flex-direction: column;
}
.yt-history__item {
  display: grid;
  grid-template-columns: auto auto 1fr auto;
  align-items: center;
  gap: 16px;
  padding: 14px 20px;
  border-left: 3px solid transparent;
  background: #fff;
  &.is-current {
    border-left-color: $yt-accent;
    background: rgba(6, 95, 212, 0.04);
  }
  & + & { border-top: 1px solid $yt-border; }
}
.yt-history__pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 12px;
  background: $yt-accent;
  color: #fff;
  white-space: nowrap;
}
.yt-history__when {
  font-size: 13px;
  line-height: 1.35;
  font-variant-numeric: tabular-nums;
}
.yt-history__date {
  color: $yt-text;
  font-weight: 500;
}
.yt-history__time {
  color: $yt-text-muted;
}
.yt-history__src {
  min-width: 0;
  font-size: 13px;
  line-height: 1.35;
}
.yt-history__src-label {
  color: $yt-text;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.yt-history__src-owner {
  color: $yt-text-muted;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.yt-history__asset-icon {
  font-size: 20px;
  color: $yt-text-muted;
}
.yt-history__empty {
  font-size: 13px;
  color: $yt-text-faint;
  padding: 24px 20px;
  text-align: center;
}
.yt-history__foot {
  padding: 12px 20px 16px;
  display: flex;
  justify-content: flex-end;
}
.yt-history__revert {
  appearance: none;
  border: 1px solid $yt-border;
  background: #fff;
  color: $yt-text-muted;
  font-size: 13px;
  font-weight: 500;
  padding: 7px 20px;
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
  &:hover:not(:disabled) {
    background: $yt-bg-sidebar;
    color: $yt-text;
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
}

.yt-issue-list { display: flex; flex-direction: column; gap: 12px; }
.yt-issue-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border: 1px solid $yt-border;
  border-radius: 4px;
  font-size: 13px;
}

@media (max-width: 1100px) {
  .yt-shares { grid-template-columns: 1fr; }
  .yt-history { position: static; }
  .yt-global-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 800px) {
  .yt-body { grid-template-columns: 1fr; }
  .yt-sidebar { border-right: 0; border-bottom: 1px solid $yt-border; }
  .yt-field-grid { grid-template-columns: 1fr; }
  .yt-yourmeta__row { grid-template-columns: 1fr; }
  .yt-section { padding: 20px 16px; }
}
</style>
